interface CategoryMatchers {
  gna: string[];
  rent: string[];
  sales: string[];
  cogs: string[];
  labor: string[];
  advertising: string[];
  controllableExpenses: string[];
  nonControllableExpenses: string[];
}

const accountMatchers: CategoryMatchers = {
  advertising: [
    'advertising',
    'marketing',
    'promotion',
    'media',
    'ad agency',
    'social media',
    'campaign',
    'billboard',
    'digital marketing',
    'seo',
    'search engine',
    'sponsored',
    'commercial',
    'branding',
  ],
  gna: [
    'administrative',
    'admin',
    'office',
    'legal',
    'professional fee',
    'insurance',
    'utilities',
    'bank fee',
    'depreciation',
    'travel',
    'entertainment',
    'supplies',
    'telephone',
    'communication',
    'training',
    'software',
    'it ',
    'technology',
    'subscription',
    'general expense',
    'corporate',
    'management fee',
    'consulting',
    'accounting',
    'dues',
    'fee',
    'utilities',
    'electricity',
    'gas',
    'water',
    'trash',
  ],

  rent: ['rent', 'lease', 'occupancy', 'property tax', 'cam ', 'common area', 'real estate', 'facility'],
  // property expenses
  // direct operating expenses

  sales: [
    'revenue',
    'sales',
    'income',
    'gift card',
    'discount',
    'promotion',
    'catering',
    'delivery',
    'service charge',
    'surcharge',
  ],

  cogs: [
    'cost of goods',
    'cogs',
    'food cost',
    'beverage cost',
    'inventory',
    'purchases',
    'supplies',
    'ingredients',
    'freight',
    'shipping',
  ],

  labor: [
    'payroll',
    'salary',
    'wage',
    'labor',
    'commission',
    'bonus',
    'overtime',
    '401k',
    'health insurance',
    'worker comp',
    'workers comp',
    'benefits',
    'personnel',
    'tips',
    'tip credit',
    'fica',
    'futa',
    'mpfmla',
    'suta',
    'emp ben',
  ],
  controllableExpenses: [],
  nonControllableExpenses: [],
};

// Priority order for categorization
const categoryPriority = [
  'sales', // Revenue accounts should be identified first
  'cogs', // Direct costs second
  'labor', // Labor costs third
  'rent', // Rent/occupancy fourth
  'advertising', // Advertising/marketing fifth
  'gna', // G&A catches remaining overhead
  'controllableExpenses',
  'nonControllableExpenses',
];

interface GLRanges {
  [category: string]: Array<{
    start: number;
    end: number;
  }>;
}

const glCodeRanges: GLRanges = {
  sales: [{ start: 4000, end: 4999 }],
  cogs: [{ start: 5000, end: 5499 }],
  labor: [{ start: 5500, end: 5999 }],
  /*   rent: [{ start: 6000, end: 6199 }],
  advertising: [{ start: 6100, end: 6199 }],
  gna: [{ start: 6200, end: 6999 }], */
  rent: [{ start: 6000, end: 8999 }],
  controllableExpenses: [{ start: 7000, end: 7999 }],
  nonControllableExpenses: [{ start: 8000, end: 8999 }],
};

export function isSyntheticAccount(title: string, glCode: number | string | null): boolean {
  // GL Code 0 is always a synthetic account
  const numericGLCode = typeof glCode === 'string' ? parseInt(glCode, 10) : glCode;
  if (numericGLCode === 0) {
    return true;
  }

  const lowerTitle = title.toLowerCase();
  const syntheticKeywords = ['total', 'subtotal', 'sum', 'summary', 'net', 'gross', 'grand total'];

  // Use word boundary checks for more accurate matching
  return syntheticKeywords.some(keyword => {
    // Create a regex with word boundaries
    const regex = new RegExp(`\\b${keyword}\\b`, 'i');
    return regex.test(lowerTitle);
  });
}

export function identifyAccountType(
  title: string,
  glCode: number | string | null,
  parentCategory: string | null = null,
  parentGLCode: number | string | null = null,
  isExpenses: 'controllable' | 'nonControllable' | null = null
): {
  category: string;
  confidence: 'high' | 'medium' | 'low';
  synthetic: boolean;
} {
  // Convert string glCodes to numbers for comparisons
  const numericGLCode = typeof glCode === 'string' ? parseInt(glCode, 10) : glCode;
  const numericParentGLCode = typeof parentGLCode === 'string' ? parseInt(parentGLCode, 10) : parentGLCode;

  // First check if it's a synthetic account
  const synthetic = isSyntheticAccount(title, glCode);
  /*   if (synthetic) {
    console.log('Synthetic account:', title, glCode);
  } */
  // If it's synthetic, we don't categorize it
  if (synthetic) {
    return {
      category: 'synthetic',
      confidence: 'high',
      synthetic: true,
    };
  }

  const lowerTitle = title.toLowerCase();
  let matchedCategory: string = parentCategory || 'uncategorized';
  let confidence: 'high' | 'medium' | 'low' = 'low';

  if (isExpenses === 'controllable') {
    matchedCategory = 'controllableExpenses';
    confidence = 'high';
    return {
      category: matchedCategory,
      confidence,
      synthetic: false,
    };
  } else if (isExpenses === 'nonControllable') {
    matchedCategory = 'nonControllableExpenses';
    confidence = 'high';
    return {
      category: matchedCategory,
      confidence,
      synthetic: false,
    };
  }

  // Original categorization logic for non-synthetic accounts
  // Check for exact matches first (highest confidence)
  for (const category of categoryPriority) {
    if (accountMatchers[category as keyof typeof accountMatchers].some(keyword => lowerTitle === keyword)) {
      matchedCategory = category;
      confidence = 'high';
      break;
    }
  }

  // If no exact match found, check for partial matches
  if (confidence === 'low') {
    for (const category of categoryPriority) {
      if (accountMatchers[category as keyof typeof accountMatchers].some(keyword => lowerTitle.includes(keyword))) {
        matchedCategory = category;
        confidence = 'medium';
        break;
      }
    }
    if (numericParentGLCode && numericGLCode && numericParentGLCode - numericGLCode < 500) {
      //console.log('///////////', parentCategory, numericGLCode, numericParentGLCode);
      matchedCategory = parentCategory || 'uncategorized';
      confidence = 'high';
    }
  }

  // If parent category matches our calculated category, increase confidence
  if (parentCategory && parentCategory === matchedCategory && confidence === 'medium') {
    confidence = 'high';
  }
  //console.log('+++++ GL Code Category (before GL code validation):', title, glCode, matchedCategory, confidence, parentCategory, parentGLCode, synthetic);

  // GL code validation remains the same
  if (numericGLCode !== null && !isNaN(numericGLCode) && parentCategory === null && confidence === 'low') {
    const glCodeCategory = categoryPriority.find(category => {
      const ranges = glCodeRanges[category as keyof typeof glCodeRanges];
      return ranges && ranges.some(range => numericGLCode >= range.start && numericGLCode <= range.end);
    });

    if (glCodeCategory) {
      if (glCodeCategory === matchedCategory) {
        confidence = 'high';
      } else if (glCodeCategory !== matchedCategory) {
        matchedCategory = glCodeCategory;
        confidence = 'medium';
      } else if (matchedCategory !== 'uncategorized') {
        confidence = 'low';
      }
    }
  }
  //console.log('----- GL Code Category:', matchedCategory, confidence, parentCategory, parentGLCode, synthetic);

  return {
    category: matchedCategory,
    confidence,
    synthetic: false,
  };
}
